import * as AgmNames from "./const/agm-names";
import * as EventNames from "./const/event-names";
import { objectValues } from "./helper";
import { Glue42Core } from "@glue42/core";
import { Glue42 } from "../../glue";
import { AppProps, Configuration, InstanceProps } from "./types";
import AppManagerImpl from "./app-manager";
import EntitlementsImpl from "./entitlements";

/** Subscribe for the AGM stream published by ACS and distributes events to applications and instances components */
export default function createDataSubscription(agm: Glue42Core.AGM.API, applications: AppManagerImpl, entitlements: EntitlementsImpl, skipIcons: boolean) {
    let subscription: Glue42Core.AGM.Subscription;
    let initiated = false;
    const start = () => {
        let resolveFunc: (config: Configuration) => void;
        let rejectFunc: (error?: any) => void;

        const resultPromise = new Promise<Configuration>((resolve, reject) => {
            resolveFunc = resolve;
            rejectFunc = reject;
        });

        agm.subscribe(AgmNames.OnEventMethodName, { arguments: { skipIcon: skipIcons }, waitTimeoutMs: 10000 })
            .then((s) => {
                subscription = s;
                subscription.onData((streamData: Glue42Core.AGM.StreamData) => {
                    const events: any = streamData.data;
                    const configuration = events.configuration ?? {};

                    const onApplicationAddedEventArgs: AppProps[] = objectValues(events[EventNames.OnApplicationAddedEvent]);
                    if (streamData.data.isSnapshot) {
                        applications.handleSnapshotAppsAdded(onApplicationAddedEventArgs);
                    } else {
                        onApplicationAddedEventArgs.forEach((item: AppProps) => applications.handleAppAdded(item));
                    }

                    objectValues(events[EventNames.OnApplicationChangedEvent])
                        .forEach((item: AppProps) => applications.handleAppUpdated(item));

                    objectValues(events[EventNames.OnApplicationRemovedEvent])
                        .forEach((item: AppProps) => applications.handleAppRemoved(item));

                    objectValues(events[EventNames.OnApplicationReadyEvent])
                        .forEach((item: AppProps) => applications.handleAppReady(item));

                    const onApplicationStartedEventArgs: InstanceProps[] = objectValues(events[EventNames.OnApplicationStartedEvent]);

                    if (streamData.data.isSnapshot) {
                        applications.handleSnapshotInstanceStarted(onApplicationStartedEventArgs);
                    } else {
                        onApplicationStartedEventArgs.forEach((item: InstanceProps) => applications.handleInstanceStarted(item));
                    }

                    objectValues(events[EventNames.OnApplicationStartFailedEvent])
                        .forEach((item: InstanceProps) => applications.handleInstanceStartFailed(item));

                    objectValues(events[EventNames.OnApplicationStoppedEvent])
                        .forEach((item: InstanceProps) => applications.handleInstanceStopped(item));

                    objectValues(events[EventNames.OnApplicationUpdatedEvent])
                        .forEach((item: InstanceProps) => applications.handleInstanceUpdated(item));

                    objectValues(events[EventNames.OnApplicationAgmServerReadyEvent])
                        .forEach((item: InstanceProps) => applications.handleInstanceAgmServerReady(item));

                    objectValues(events[EventNames.OnBranchChangedEvent])
                        .forEach((item: Glue42.AppManager.Branch) => entitlements.handleBranchModified(item));

                    objectValues(events[EventNames.OnBranchesModifiedEvent])
                        .forEach((item: Glue42.AppManager.Branch[]) => entitlements.handleBranchesModified(item));

                    if (!initiated) {
                        initiated = true;
                        const hasMyAppInSnapShot = onApplicationAddedEventArgs.some((a) => a.Name === agm.instance.application);
                        const hasMyInstanceInSnapShot = onApplicationStartedEventArgs.some((i) => i.Id === agm.instance.instance);
                        if (hasMyAppInSnapShot) {
                            if (hasMyInstanceInSnapShot) {
                                resolveFunc(configuration);
                            } else {
                                const un = applications.onInstanceStarted((i) => {
                                    if (i.id === agm.instance.instance) {
                                        un();
                                        resolveFunc(configuration);
                                    }
                                });
                            }
                        } else {
                            resolveFunc(configuration);
                        }
                    }
                });

                subscription.onFailed((err: any) => rejectFunc(err));

            })
            .catch((err: Error) => rejectFunc(`Error subscribing for ${AgmNames.OnEventMethodName} stream. Err: ${err.message ?? JSON.stringify(err)}`));

        return resultPromise;
    };

    const stop = () => {
        if (subscription) {
            subscription.close();
        }
    };

    return {
        start,
        stop
    };
}
